"use client";
// The data room (2026-09-16), as a firm sees it: the gate — the password we gave them, their name — then
// the room: folders on the left, the files they may see. View-only: a file opens in our own viewer.
import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { DataroomRoom } from "@shapely/schema";
import { Emblem } from "@/components/Brand";
import { clearSession, drFetch, enter, fmtBytes, getSession, RoomError, type RoomSession } from "@/lib/dataroom";
import { roomHref } from "@/lib/dataroom-host";

export default function DataroomPage() {
  const [session, setSession] = useState<RoomSession | null | undefined>(undefined);
  useEffect(() => { setSession(getSession()); }, []);
  if (session === undefined) return <p className="dr-msg">…</p>;
  if (!session) return <Gate onEntered={setSession} />;
  return <Room session={session} onLeave={() => { clearSession(); setSession(null); }} />;
}

function Gate({ onEntered }: { onEntered: (s: RoomSession) => void }) {
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || busy) return;
    setBusy(true); setErr(null);
    try { onEntered(await enter(password, name.trim(), email.trim())); }
    catch (x) { setErr(x instanceof RoomError && x.status === 429 ? "Too many tries. Wait ten minutes and try again." : x instanceof RoomError && x.status === 401 ? "That password does not open this room. Check it against the one we sent you." : "Something went wrong on our side. Try again in a moment."); }
    finally { setBusy(false); }
  };
  return (
    <>
      <header className="dr-bar"><span className="brand"><Emblem size={16} /> Shapely Labs</span><span className="title">Data room</span></header>
      <form className="dr-gate" onSubmit={(e) => void go(e)}>
        <Emblem size={30} />
        <h1>Shapely Labs data room</h1>
        <p>For the investors we are talking to. Enter with the password we sent your firm.</p>
        <label>Password<input className="pw" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus autoComplete="off" spellCheck={false} placeholder="xxxx-xxxx-xxxx-xxxx" /></label>
        <label>Your name<input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="so we know who read what" /></label>
        <label>Email <span style={{ color: "#6f7484" }}>(optional)</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
        {err && <div className="err">{err}</div>}
        <button type="submit" disabled={busy || !password.trim()}>{busy ? "One moment…" : "Enter"}</button>
      </form>
      <footer className="dr-foot">Shapely Labs SAS · confidential</footer>
    </>
  );
}

const KIND: Record<string, string> = { "application/pdf": "PDF", "image/png": "IMG", "image/jpeg": "IMG" };

function Room({ session, onLeave }: { session: RoomSession; onLeave: () => void }) {
  const path = usePathname() ?? "/";
  const [room, setRoom] = useState<DataroomRoom | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  useEffect(() => {
    drFetch("/dr/room").then(async (r) => { if (!r.ok) throw new RoomError(r.status, "could not open the room"); const j = (await r.json()) as DataroomRoom; setRoom(j); setFolder((f) => f ?? j.folders[0]?.name ?? null); })
      .catch((e: RoomError) => { if (e.status === 401) onLeave(); else setErr("The room could not be opened just now. Try again in a moment."); });
  }, [session.token]);   // eslint-disable-line react-hooks/exhaustive-deps
  const cur = room?.folders.find((f) => f.name === folder) ?? room?.folders[0] ?? null;
  return (
    <>
      <header className="dr-bar">
        <span className="brand"><Emblem size={16} /> Shapely Labs</span>
        <span className="title">Data room · {session.firm}</span>
        <button className="leave" onClick={onLeave} title="Close the room in this tab">Leave</button>
      </header>
      {err && <p className="dr-msg">{err}</p>}
      {!room && !err && <p className="dr-msg">Opening…</p>}
      {room && (
        <div className="dr-room">
          <nav className="dr-nav">
            <h2>Folders</h2>
            {room.folders.map((f) => <button key={f.name} className={f.name === cur?.name ? "on" : ""} onClick={() => setFolder(f.name)}>{f.name || "General"}<span className="n">{f.files.length}</span></button>)}
            {room.folders.length === 0 && <p className="dr-empty" style={{ padding: "6px 10px" }}>Nothing here yet.</p>}
          </nav>
          <section className="dr-list">
            <h1>{cur ? (cur.name || "General") : "Data room"}</h1>
            <p className="lede">Shared with {session.firm}{session.name ? ` · ${session.name}` : ""}. Everything here is confidential.</p>
            {cur?.files.map((f) => (
              <Link key={f.id} className="dr-file" href={roomHref(`/f/${f.id}`, path)}>
                <span className="ico">{KIND[f.contentType] ?? "FILE"}</span>
                <span><div className="nm">{f.name}</div><div className="sub">{[f.pages ? `${f.pages} page${f.pages === 1 ? "" : "s"}` : null, fmtBytes(f.bytes)].filter(Boolean).join(" · ")}</div></span>
                <span className="go">open →</span>
              </Link>
            ))}
            {cur && cur.files.length === 0 && <p className="dr-empty">Nothing in this folder yet.</p>}
          </section>
        </div>
      )}
      <footer className="dr-foot">Shapely Labs SAS · confidential · shared with {session.firm}</footer>
    </>
  );
}
